import { Component, OnInit } from '@angular/core';
import { Subject, Subscription, takeUntil } from "rxjs";
import { MenuItem, MessageService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { MenuService } from "../../services/menus/menu.service";
import { AuthenticationService } from "../../../share-modul/services/authentication/authentication.service";
import { TaxRateModel } from "../../models/tax-rates/tax-rate.model";
import { AddOrEditTaxRateComponent } from "./add-or-edit-tax-rate/add-or-edit-tax-rate.component";
import { DeleteTaskRateComponent } from "./delete-task-rate/delete-task-rate.component";
import { CurrentVenueHelperService } from 'src/app/share-modul/services/current-venue-helper/current-venue.helper.service';

@Component({
  selector: 'app-tax-rates',
  templateUrl: './tax-rates.component.html',
  styleUrls: ['./tax-rates.component.scss']
})
export class TaxRatesComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public listAllTaxRates: Array<TaxRateModel> = new Array<TaxRateModel>();
  public spinner: boolean;
  public companyId: string;
  public items: { [name: string]: MenuItem[] } = {};
  //#endregion

  //#region Constructor
  constructor(
    public dialogService: DialogService,
    public router: Router,
    private messageService: MessageService,
    private translate: TranslateService,
    private menuService: MenuService,
    private authenticationService: AuthenticationService
  ) {
    this.translate.stream('HEADER.APP_LANGUAGE').subscribe(() => {
      this.addFunctionToAllTaxRates();
    });
  }

  public ngOnInit(): void {
    this.companyId = this.authenticationService.getCompanyId();
    this.loadAllTaxRates();
  }
  //#endregion

  //#region Public Methods
  public loadAllTaxRates(): void {
    this.listAllTaxRates = new Array<TaxRateModel>();
    this.spinner = true;
    this.menuService.listAllTaxRates()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.listAllTaxRates = response;
          this.addFunctionToAllTaxRates();
          this.spinner = false;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  public addTaxRate(): void {
    const ref = this.dialogService.open(AddOrEditTaxRateComponent, {
      width: '500px',
      header: this.translate.instant('TAX_RATE.CREATE_TAX_RATE'),
      styleClass: 'dialog_menus , ml_200, '
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadAllTaxRates();
        this.messageService.add({severity: 'success', summary: this.translate.instant("TAX_RATE.TAX_RATE_ADDED"), life: 3000});
      }
    });
  }

  public editTaxRate(taxRate: TaxRateModel): void {
    const ref = this.dialogService.open(AddOrEditTaxRateComponent, {
      data: {
        id: taxRate.id
      },
      width: '500px',
      header: this.translate.instant("TAX_RATE.EDIT_TAX_RATE"),
      styleClass: 'dialog_menus , ml_200, '
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadAllTaxRates();
        this.messageService.add({severity: 'success', summary: this.translate.instant("TAX_RATE.TAX_RATE_EDITED"), life: 3000});
      }
    });
  }

  public deleteTaxRate(id: string): void {
    const ref = this.dialogService.open(DeleteTaskRateComponent, {
      data: {
        id: id
      },
      header: this.translate.instant("TAX_RATE.DELETE_TAX_RATE"),
      styleClass: 'dialog_long , ml_200, mb_15'
    });
    ref.onClose.subscribe((res: {item: any, error?: any}) => {
      if (res.item) {
        this.loadAllTaxRates();
        this.messageService.add({severity: 'success', summary: this.translate.instant("TAX_RATE.TAX_RATE_DELETED"), life: 3000});
      } else {
        if (res.error) {
          if (res.error.error.StatusCode === -306) {
            this.messageService.add({severity: 'error', summary: res.error.error.Message, life: 2000});
          } else {
            this.messageService.add({severity: 'error', summary: "System error", life: 2000});
          }
        }
      }
    });
  }

  public addFunctionToAllTaxRates(): void {
    this.listAllTaxRates.forEach(taxRate => {
      this.items[taxRate.id] = [{
        label: this.translate.instant('EDIT'),
        command: () => {
          this.editTaxRate(taxRate);
        }
      },
        {
          label: this.translate.instant('DELETE'),
          command: () => {
            this.deleteTaxRate(taxRate.id);
          }
        }];
    });
  }
  //#endregion
}
